import React from 'react';

const FileTable = ({
  items,
  selectedItems,
  viewMode,
  sortBy,
  sortOrder,
  clipboardItems,
  clipboardOperation,
  onToggleSelection,
  onCheckboxAreaClick,
  onSelectAll,
  onSort,
  onItemDoubleClick,
  onContextMenu
}) => {
  const getFileIcon = (item) => {
    if (item.isFolder) {
      return '📁';
    }
    
    const ext = item.name.split('.').pop()?.toLowerCase();
    const iconMap = {
      pdf: '📄',
      doc: '📝',
      docx: '📝',
      xls: '📊',
      xlsx: '📊',
      ppt: '📊',
      pptx: '📊',
      jpg: '🖼️',
      jpeg: '🖼️',
      png: '🖼️',
      gif: '🖼️',
      zip: '📦',
      rar: '📦',
      txt: '📃',
      dwg: '📐',
      dxf: '📐'
    };
    
    return iconMap[ext] || '📄';
  };

  const isItemInClipboard = (item) => {
    return clipboardItems?.some(clipItem => clipItem.id === item.id);
  };

  const getSortIcon = (column) => {
    if (sortBy !== column) return null;
    return sortOrder === 'asc' ? ' ▲' : ' ▼';
  };

  if (viewMode === 'details') {
    return (
      <div 
        className="files-table-container"
        onContextMenu={(e) => {
          // Si el clic derecho es en el contenedor vacío (no en una fila)
          if (e.target.classList.contains('files-table-container') || 
              e.target.tagName === 'TD' && items.length === 0) {
            onContextMenu(null, e);
          }
        }}
      >
        <table className="files-table windows-table">
          <thead>
            <tr>
              <th style={{ width: '40px' }}>
                <input
                  type="checkbox"
                  checked={selectedItems.size === items.length && items.length > 0}
                  onChange={onSelectAll}
                  title="Seleccionar todo (Ctrl+A)"
                />
              </th>
              <th 
                className="sortable" 
                onClick={() => onSort('name')}
                style={{ cursor: 'pointer' }}
              >
                Nombre{getSortIcon('name')}
              </th>
              <th 
                className="sortable" 
                onClick={() => onSort('size')}
                style={{ width: '120px', cursor: 'pointer' }}
              >
                Tamaño{getSortIcon('size')}
              </th>
              <th 
                className="sortable" 
                onClick={() => onSort('date')}
                style={{ width: '180px', cursor: 'pointer' }}
              >
                Fecha modificación{getSortIcon('date')}
              </th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr>
                <td colSpan="4" style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                  <div>
                    <i className="fas fa-folder-open" style={{ fontSize: '48px', marginBottom: '16px' }}></i>
                    <p>Esta carpeta está vacía</p>
                  </div>
                </td>
              </tr>
            ) : (
              items.map((item) => {
                const isSelected = selectedItems.has(item.id);
                const isInClipboard = isItemInClipboard(item);
                const isCut = isInClipboard && clipboardOperation === 'cut';
                
                return (
                  <tr
                    key={item.id}
                    className={`${isSelected ? 'selected' : ''} ${item.isFolder ? 'folder-row' : 'file-row'}`}
                    style={{
                      opacity: isCut ? 0.5 : 1,
                      background: isInClipboard ? (clipboardOperation === 'cut' ? 'rgba(255, 107, 107, 0.05)' : 'rgba(79, 70, 229, 0.05)') : undefined
                    }}
                    onClick={(e) => onToggleSelection(item.id, e)}
                    onDoubleClick={() => onItemDoubleClick(item)}
                    onContextMenu={(e) => onContextMenu(item, e)}
                  >
                    <td
                      className="checkbox-cell"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (e.shiftKey || e.ctrlKey) {
                          onToggleSelection(item.id, e);
                        } else if (onCheckboxAreaClick) {
                          onCheckboxAreaClick(item.id);
                        }
                      }}
                      style={{ cursor: 'pointer' }}
                      title="Clic: toggle. Shift+clic: rango. Ctrl+clic: toggle"
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        readOnly
                        tabIndex={-1}
                      />
                    </td>
                    <td>
                      <div className="file-info-cell">
                        <span className="file-icon">{getFileIcon(item)}</span>
                        <span className="file-name">
                          {item.name}
                          {item.version > 1 && (
                            <span className="version-badge" title={`Versión ${item.version}`}>
                              v{item.version}
                            </span>
                          )}
                        </span>
                      </div>
                    </td>
                    <td>{item.size || '-'}</td>
                    <td>
                      {item.lastModified 
                        ? new Date(item.lastModified).toLocaleString('es-ES', {
                            year: 'numeric',
                            month: '2-digit',
                            day: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit'
                          })
                        : '-'
                      }
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    );
  }

  // Vista Grid
  return (
    <div className="files-grid-container">
      <div className="files-grid">
        {items.length === 0 ? (
          <div className="empty-folder">
            <i className="fas fa-folder-open" style={{ fontSize: '64px', color: '#ccc', marginBottom: '16px' }}></i>
            <p style={{ color: '#999' }}>Esta carpeta está vacía</p>
          </div>
        ) : (
          items.map((item) => {
            const isSelected = selectedItems.has(item.id);
            const isInClipboard = isItemInClipboard(item);
            const isCut = isInClipboard && clipboardOperation === 'cut';
            
            return (
              <div
                key={item.id}
                className={`grid-item ${isSelected ? 'selected' : ''}`}
                style={{
                  opacity: isCut ? 0.5 : 1,
                  background: isInClipboard ? (clipboardOperation === 'cut' ? 'rgba(255, 107, 107, 0.05)' : 'rgba(79, 70, 229, 0.05)') : undefined
                }}
                onClick={(e) => onToggleSelection(item.id, e)}
                onDoubleClick={() => onItemDoubleClick(item)}
                onContextMenu={(e) => onContextMenu(item, e)}
              >
                <div
                  className="grid-item-checkbox"
                  style={{ cursor: 'pointer' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (e.shiftKey || e.ctrlKey) {
                      onToggleSelection(item.id, e);
                    } else if (onCheckboxAreaClick) {
                      onCheckboxAreaClick(item.id);
                    }
                  }}
                  title="Clic: toggle. Shift+clic: rango. Ctrl+clic: toggle"
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    readOnly
                    tabIndex={-1}
                  />
                </div>
                <div className="grid-item-icon">
                  <span style={{ fontSize: '48px' }}>{getFileIcon(item)}</span>
                </div>
                <div className="grid-item-name" title={item.name}>
                  {item.name}
                  {item.version > 1 && (
                    <span className="version-badge-small">v{item.version}</span>
                  )}
                </div>
                {!item.isFolder && (
                  <div className="grid-item-size">{item.size}</div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default FileTable;
